/**
 * Billing metric for invoice quantity resolution (from ShipmentType / invoice line).
 * @typedef {'kg' | 'cbm'} BillingMetric
 */

/**
 * Invoice nested shipment line fields used for PDF / detail qty display.
 * Prefer these over raw weight / volumetric_weight.
 * @typedef {Object} InvoiceShipmentLine
 * @property {string|null|undefined} invoice_quantity
 * @property {string|undefined} invoice_quantity_unit
 * @property {string|undefined} invoice_quantity_unit_ar
 * @property {BillingMetric|undefined} billing_metric
 * @property {string|null|undefined} weight
 */

/**
 * Language-appropriate billed unit name (Kg, CBM, or the type's custom label).
 * The invoice PDF uses this name as the billed-qty column title.
 *
 * @param {InvoiceShipmentLine|null|undefined} line
 * @param {'en'|'ar'} [lang='en']
 * @returns {string}
 */
export function invoiceQtyUnit(line, lang = 'en') {
  // Missing line has no unit to print.
  if (!line) return '';
  // Arabic prefers the Arabic label, then the English label.
  if (lang === 'ar') {
    return line.invoice_quantity_unit_ar || line.invoice_quantity_unit || '';
  }
  // English (and other locales) use the English unit label.
  return line.invoice_quantity_unit || '';
}

/**
 * Distinct billed-unit names on an invoice, in first-seen order.
 * One entry means the PDF column title is that name alone.
 *
 * @param {InvoiceShipmentLine[]|null|undefined} lines
 * @param {'en'|'ar'} [lang='en']
 * @returns {string[]}
 */
export function invoicePdfBilledQtyUnits(lines, lang = 'en') {
  // Distinct unit labels in first-seen order.
  const units = [];
  // Skip a label that already appears on an earlier line.
  const seen = new Set();
  // Walk each shipment line on the invoice.
  (lines || []).forEach((line) => {
    // Language-specific unit name for this line.
    const unit = invoiceQtyUnit(line, lang);
    // Blank or duplicate labels do not change the title.
    if (!unit || seen.has(unit)) return;
    // Remember this label so later lines do not repeat it.
    seen.add(unit);
    // Keep the label for the column title.
    units.push(unit);
  });
  // Caller joins these when an invoice mixes units.
  return units;
}

/**
 * Format billed invoice quantity with the language-appropriate unit.
 * Uses API-resolved invoice_* fields (live from shipment type), not type names.
 *
 * @param {InvoiceShipmentLine|null|undefined} line
 * @param {'en'|'ar'} [lang='en']
 * @returns {string}
 */
export function formatInvoiceQty(line, lang = 'en') {
  // Missing line has nothing to format.
  if (!line) return '';

  // Resolve the unit label for the active language.
  const unit = invoiceQtyUnit(line, lang);

  // No quantity stored: the unit name is the whole label.
  const qty = line.invoice_quantity;
  if (qty == null || qty === '') {
    return unit;
  }

  // Quantity plus unit, or the bare number when no unit is set.
  return unit ? `${qty} ${unit}` : String(qty);
}

/**
 * Format a shipment weight number for narrow PDF cells (grouped, trims trailing zeros).
 *
 * @param {string|number|null|undefined} weight
 * @param {'en'|'ar'} [lang='en']
 * @returns {string}
 */
export function formatInvoiceWeightNumber(weight, lang = 'en') {
  if (weight == null || weight === '') return '';

  const num = Number(weight);
  if (!Number.isFinite(num)) return String(weight);

  const locale = lang === 'ar' ? 'ar' : 'en';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(num);
}

/**
 * Format shipment actual weight for the invoice PDF weight column.
 *
 * @param {{ weight?: string|null }} line
 * @param {'en'|'ar'} [lang='en']
 * @returns {string}
 */
export function formatInvoiceWeightKg(line, lang = 'en') {
  const value = formatInvoiceWeightNumber(line?.weight, lang);
  if (!value) return '';

  const kgUnit =
    lang === 'ar'
      ? DEFAULT_KG_UNIT_LABELS.invoice_unit_label_ar
      : DEFAULT_KG_UNIT_LABELS.invoice_unit_label_en;

  return `${value} ${kgUnit}`;
}

/** True when a shipment line has a weight value for the invoice PDF Kg column. */
export function hasInvoiceWeightKg(line) {
  if (!line) return false;
  const weight = line.weight;
  return weight != null && weight !== '';
}

/** True when any shipment on the invoice should show the Kg column. */
export function invoicePdfShowsWeightColumn(shipments) {
  return (shipments || []).some(hasInvoiceWeightKg);
}

/** Default unit labels when billing_metric is kg. */
export const DEFAULT_KG_UNIT_LABELS = {
  invoice_unit_label_en: 'Kg',
  invoice_unit_label_ar: 'كجم',
};

/** Default unit labels when billing_metric is cbm. */
export const DEFAULT_CBM_UNIT_LABELS = {
  invoice_unit_label_en: 'CBM',
  invoice_unit_label_ar: 'م³',
};

/**
 * True when labels still match the kg defaults (safe to auto-fill on metric switch).
 * @param {{ invoice_unit_label_en?: string, invoice_unit_label_ar?: string }} form
 */
export function hasKgDefaultUnitLabels(form) {
  const en = (form?.invoice_unit_label_en || '').trim();
  const ar = (form?.invoice_unit_label_ar || '').trim();
  return (
    (!en || en === DEFAULT_KG_UNIT_LABELS.invoice_unit_label_en) &&
    (!ar || ar === DEFAULT_KG_UNIT_LABELS.invoice_unit_label_ar)
  );
}

/**
 * True when labels still match the cbm defaults (safe to auto-fill on metric switch).
 * @param {{ invoice_unit_label_en?: string, invoice_unit_label_ar?: string }} form
 */
export function hasCbmDefaultUnitLabels(form) {
  const en = (form?.invoice_unit_label_en || '').trim();
  const ar = (form?.invoice_unit_label_ar || '').trim();
  return (
    (!en || en === DEFAULT_CBM_UNIT_LABELS.invoice_unit_label_en) &&
    (!ar || ar === DEFAULT_CBM_UNIT_LABELS.invoice_unit_label_ar)
  );
}
